import { z } from "zod";

// ESI's authorization server (openspec change native-auth-server): the consent screen's data.

/** The resources a native client can be authorized for (RFC 8707). */
export const OAUTH_RESOURCE_NAMES = ["api", "mcp"] as const;
export type OauthResourceName = (typeof OAUTH_RESOURCE_NAMES)[number];

/**
 * The scopes of each resource, as suffixes of its URL (`<publicUrl>/mcp/write`). `access` is
 * always granted; `write` (MCP only, mcp-write-tools D2) only when the client asked for it and
 * the user approved a consent screen naming it.
 */
export const OAUTH_RESOURCE_SCOPES = {
  api: ["access"],
  mcp: ["access", "write"],
} as const satisfies Record<OauthResourceName, readonly string[]>;
export type OauthScopeName = (typeof OAUTH_RESOURCE_SCOPES)[OauthResourceName][number];

/** A resource's scope: `<resourceUrl>/<name>`. */
export const oauthScope = (resourceUrl: string, name: OauthScopeName) => `${resourceUrl}/${name}`;

/** `GET /api/oauth/interaction/:uid/details`: what the user is asked to allow. */
export const oauthConsentSchema = z.object({
  uid: z.string(),
  /**
   * The client: its name and, for a client ID metadata document client, the URL's host shown
   * beside it (null for ESI's own client). Its `logo_uri` is never shown (D2).
   */
  client: z.object({ id: z.string(), name: z.string(), host: z.string().nullable() }),
  /** Where the code goes: the redirect URI's host and port (`127.0.0.1:53682`). */
  redirectHost: z.string(),
  resource: z.enum(OAUTH_RESOURCE_NAMES),
  /**
   * The resource scopes the client asks for (mcp-write-tools D2): `access` always, `write` when
   * requested. Optional for one release: an older API omits it (read only).
   */
  scopes: z.array(z.enum(["access", "write"])).optional(),
  tenant: z.object({ name: z.string() }),
  user: z.object({ email: z.string(), displayName: z.string().nullable() }),
});
export type OauthConsent = z.infer<typeof oauthConsentSchema>;

/**
 * `POST …/confirm` of the consent screen: `write` says the screen named the write permission and
 * the user approved it. Without it (an older SPA, or no body), only `access` is granted.
 */
export const oauthConfirmSchema = z.object({ write: z.boolean().optional() }).catch({});
export type OauthConfirm = z.infer<typeof oauthConfirmSchema>;

/** `POST /api/oauth/verify/lookup` and the device page's steps (D1). */
export const deviceCodeInputSchema = z.object({ userCode: z.string().trim().min(1).max(32) });
export type DeviceCodeInput = z.infer<typeof deviceCodeInputSchema>;

/** `POST /api/oauth/verify/confirm`: the code, and `write` as on the consent screen. */
export const deviceConfirmInputSchema = deviceCodeInputSchema.extend({
  write: z.boolean().optional(),
});
export type DeviceConfirmInput = z.infer<typeof deviceConfirmInputSchema>;

/** What the user is asked to approve on ESI's device page (anti-phishing details, D1). */
export const deviceAuthorizationSchema = z.object({
  /** The code as shown on the client's screen (`BCDF-GHJK`). */
  userCode: z.string(),
  client: z.object({ id: z.string(), name: z.string(), host: z.string().nullable() }),
  resource: z.enum(OAUTH_RESOURCE_NAMES),
  /** As on the consent screen: the resource scopes the client asks for. */
  scopes: z.array(z.enum(["access", "write"])).optional(),
  /** Where and when the client started: CloudFront's viewer country only, never the address. */
  origin: z.object({ country: z.string().nullable(), startedAt: z.string() }),
  tenant: z.object({ name: z.string() }),
  user: z.object({ email: z.string(), displayName: z.string().nullable() }),
});
export type DeviceAuthorization = z.infer<typeof deviceAuthorizationSchema>;

/** The device page's decision outcome. */
export const deviceDecisionSchema = z.object({ status: z.enum(["approved", "denied"]) });
export type DeviceDecision = z.infer<typeof deviceDecisionSchema>;

/** `POST …/confirm` and `…/deny`: where the browser goes next (the client's redirect, via the
 * authorization server). */
export const oauthDecisionSchema = z.object({ redirectTo: z.string() });
export type OauthDecision = z.infer<typeof oauthDecisionSchema>;

/** The subject prefix of users signed in through ESI's own authorization server (D3). */
export const ESI_SUBJECT_PREFIX = "esi|";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The user ID of an `esi|<userId>` subject, or of a bare user ID; null when malformed. */
export function esiUserId(subjectOrId: string): string | null {
  const id = subjectOrId.startsWith(ESI_SUBJECT_PREFIX)
    ? subjectOrId.slice(ESI_SUBJECT_PREFIX.length)
    : subjectOrId;
  return UUID.test(id) ? id : null;
}

/** Where the authorization server is mounted; its issuer is `<publicUrl>/api/oauth` (D1). */
export const OAUTH_ISSUER_PATH = "/api/oauth";

/**
 * The provider's endpoints, relative to its mount (its `routes`): named, not left to the
 * defaults, because the edge's WAF rules and the BFF guards match these exact paths.
 */
export const OAUTH_ROUTES = {
  authorization: "/auth",
  token: "/token",
  revocation: "/revoke",
  deviceAuthorization: "/device/auth",
  codeVerification: "/device",
} as const;

/** The authorization and token endpoints' paths; the edge's WAF exceptions name them too. */
export const OAUTH_AUTHORIZATION_PATH = `${OAUTH_ISSUER_PATH}${OAUTH_ROUTES.authorization}`;
export const OAUTH_TOKEN_PATH = `${OAUTH_ISSUER_PATH}${OAUTH_ROUTES.token}`;
export const OAUTH_REVOCATION_PATH = `${OAUTH_ISSUER_PATH}${OAUTH_ROUTES.revocation}`;
export const OAUTH_DEVICE_AUTHORIZATION_PATH = `${OAUTH_ISSUER_PATH}${OAUTH_ROUTES.deviceAuthorization}`;
/** The provider's own code entry page: it only sends the browser to {@link DEVICE_PAGE_PATH}. */
export const OAUTH_CODE_VERIFICATION_PATH = `${OAUTH_ISSUER_PATH}${OAUTH_ROUTES.codeVerification}`;

/** ESI's device verification page (the SPA), where `verification_uri` points (D1). */
export const DEVICE_PAGE_PATH = "/device";
/** ESI's device verification API (lookup, confirm, deny), behind the BFF guards. */
export const OAUTH_DEVICE_VERIFY_PATH = `${OAUTH_ISSUER_PATH}/verify`;

/** The first-party native client's ID (`esi-cli`): always allowed by a tenant's policy. */
export const ESI_CLI_CLIENT_ID = "esi-cli";

/**
 * ESI's first-party clients: the only ones that may obtain the `/api` resource (#160). Every
 * other client (client ID metadata documents, harnesses) gets `/mcp` only, where the MCP
 * controls apply (`mcp.enabled`, the classification ceiling, MCP audit and throttles). Not the
 * tenant's `auth.nativeClients` allowlist, which says which harnesses may connect at all.
 */
export const FIRST_PARTY_CLIENT_IDS: ReadonlySet<string> = new Set([ESI_CLI_CLIENT_ID]);

/**
 * Whether `clientId` may hold tokens for `resource`: `/api` is first-party only (#160), and only
 * where the deployment issues `/api` bearer tokens at all (`apiBearer`, `ESI_API_BEARER`, off by
 * default until a first-party CLI ships, #174). `/mcp` is unaffected.
 */
export const clientMayUseResource = (
  clientId: string,
  resource: OauthResourceName,
  apiBearer: boolean,
) => resource === "mcp" || (apiBearer && FIRST_PARTY_CLIENT_IDS.has(clientId));

/** Device flow (RFC 8628, D1): lifetime of a device code and the polling interval, seconds. */
export const DEVICE_CODE_TTL_SECONDS = 10 * 60;
export const DEVICE_POLL_INTERVAL_SECONDS = 5;

/**
 * The loopback redirect URIs of the first-party native client (`esi-cli`); the provider ignores
 * their port (RFC 8252 §7.3). The edge's WAF exceptions are derived from them.
 */
export const OAUTH_LOOPBACK_REDIRECT_URIS: readonly string[] = [
  "http://127.0.0.1/callback",
  "http://[::1]/callback",
];

/**
 * The loopback hosts (WHATWG `URL.hostname`) a native client's `http` redirect URI may name, any
 * port (RFC 8252 §7.3): those of `esi-cli`'s URIs and `localhost`, which client ID metadata
 * documents use (Claude Code). The provider takes them for client ID metadata documents and the
 * edge's WAF exceptions are derived from them; `esi-cli` itself registers the IP literals only.
 */
export const OAUTH_LOOPBACK_HOSTS: readonly string[] = ["127.0.0.1", "[::1]", "localhost"];

/**
 * The authorization server's discovery documents at the root (RFC 8414 path insertion and
 * OpenID's form): served by the API, routed there by CloudFront.
 */
export const OAUTH_DISCOVERY_PATHS: readonly string[] = [
  `/.well-known/oauth-authorization-server${OAUTH_ISSUER_PATH}`,
  `/.well-known/openid-configuration${OAUTH_ISSUER_PATH}`,
];

/**
 * The payload model under which ESI records how the session that approved a grant signed in
 * (`@esi/auth` writes it, `@esi/db` deletes it with the user's grants).
 */
export const OAUTH_GRANT_SIGN_IN_MODEL = "EsiGrantSignIn";

/** The payload model of cached client ID metadata documents (D2), shared by every instance. */
export const OAUTH_CIMD_MODEL = "CimdClient";

// ── Native clients (openspec change native-device-and-cimd) ───────────────────────────────

/** A native client's ID: ESI's own (`esi-cli`) or a client ID metadata document URL. */
export const nativeClientIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(2048)
  .refine(
    (id) => id === ESI_CLI_CLIENT_ID || (URL.canParse(id) && new URL(id).protocol === "https:"),
    "Use esi-cli or an https client ID metadata document URL",
  );

/**
 * `auth.nativeClients` (D3): any client, or only those listed (exact client IDs). ESI's own
 * command-line client is always allowed, listed or not.
 */
export const nativeClientsPolicySchema = z.strictObject({
  mode: z.enum(["any", "allowlist"]),
  clients: z.array(nativeClientIdSchema).max(100),
});
export type NativeClientsPolicy = z.infer<typeof nativeClientsPolicySchema>;

/**
 * The policy's default: any client (decided 2026-10-22). The key is tenant-wide and never an
 * instance policy, so a tenant's Reset always brings this back.
 */
export const NATIVE_CLIENTS_DEFAULT: NativeClientsPolicy = { mode: "any", clients: [] };

/** A client ID as URLs compare (host case, default port): an admin's spelling still matches. */
export function canonicalClientId(clientId: string): string {
  return URL.canParse(clientId) ? new URL(clientId).href : clientId;
}

/** Whether a tenant's policy lets its users authorize `clientId`. */
export function nativeClientAllowed(policy: NativeClientsPolicy, clientId: string): boolean {
  if (clientId === ESI_CLI_CLIENT_ID || policy.mode === "any") {
    return true;
  }
  const wanted = canonicalClientId(clientId);
  return policy.clients.some((listed) => canonicalClientId(listed) === wanted);
}

/** A native or MCP client a user authorized (D4): `GET /api/account/connected-apps`. */
export const connectedAppSchema = z.object({
  grantId: z.string(),
  client: z.object({
    id: z.string(),
    /** Its name as last fetched (ESI's own client: "ESI CLI"); null when not known. */
    name: z.string().nullable(),
    /** The client ID's host for a metadata-document client; null for ESI's own client. */
    host: z.string().nullable(),
  }),
  resources: z.array(z.enum(OAUTH_RESOURCE_NAMES)),
  /**
   * The resource scopes the grant holds, across its resources (`write`: the client may save
   * notes and propose wiki drafts, mcp-write-tools D2). Optional for one release.
   */
  scopes: z.array(z.enum(["access", "write"])).optional(),
  createdAt: z.string(),
  /** When it last refreshed its tokens; null when it never did. */
  lastRefreshedAt: z.string().nullable(),
});
export type ConnectedApp = z.infer<typeof connectedAppSchema>;

/** `GET /api/admin/oauth-clients?id=`: a client's name and domain (the allowlist editor). */
export const nativeClientInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  host: z.string().nullable(),
});
export type NativeClientInfo = z.infer<typeof nativeClientInfoSchema>;

/** Tenant policy refusals shared by device authorization and native-client grants. */
export const NATIVE_CLIENT_REFUSAL_CODES = ["client_not_allowed", "device_grant_disabled"] as const;
export type NativeClientRefusal = (typeof NATIVE_CLIENT_REFUSAL_CODES)[number];
export const DEVICE_REFUSAL_CODES = [
  "operator",
  "inactive",
  "mfa_required",
  ...NATIVE_CLIENT_REFUSAL_CODES,
] as const;
export type DeviceRefusal = (typeof DEVICE_REFUSAL_CODES)[number];
